import {HttpErrorResponse, HttpInterceptorFn} from '@angular/common/http';
import {inject} from '@angular/core';
import {Router} from '@angular/router';
import {Auth} from '@angular/fire/auth';
import {catchError, from, switchMap, throwError} from 'rxjs';
import {AuthTokenService} from '../services/auth-token.service';
import {environment} from '../../environments/environment';
import {NavigateService} from '../services/navigate.service';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authTokenService = inject(AuthTokenService);
  const auth = inject(Auth);
  const navigate = inject(NavigateService);
  const apiBaseUrl = environment.apiBaseUrl;

  // Only inject auth header for API requests
  if (!req.url.startsWith(apiBaseUrl)) {
    return next(req);
  }

  const token = authTokenService.token;

  // If no token, try to refresh or redirect to login
  if (!token) {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      navigate.login();
      return throwError(() => new Error('Not authenticated'));
    }

    // Try to force refresh the token
    return from(currentUser.getIdToken(true)).pipe(
      switchMap(refreshedToken => {
        const authReq = req.clone({
          setHeaders: {
            Authorization: `Bearer ${refreshedToken}`
          }
        });
        return next(authReq);
      }),
      catchError(error => {
        navigate.login();
        return throwError(() => error);
      })
    );
  }

  // Clone request with auth header
  const authReq = req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`
    }
  });

  // Handle 401 responses by attempting token refresh
  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        const currentUser = auth.currentUser;

        if (!currentUser) {
          navigate.login();
          return throwError(() => error);
        }

        // Try to refresh token and retry request
        return from(currentUser.getIdToken(true)).pipe(
          switchMap(refreshedToken => {
            const retryReq = req.clone({
              setHeaders: {
                Authorization: `Bearer ${refreshedToken}`
              }
            });
            return next(retryReq);
          }),
          catchError(refreshError => {
            navigate.login();
            return throwError(() => refreshError);
          })
        );
      }

      return throwError(() => error);
    })
  );
};
